import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { Mail, ArrowRight, Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_africa-ventures/artifacts/mjqy16nm_image.png";

export default function LoginPage() {
  const { login, verifyOTP } = useAuth();
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpForTesting, setOtpForTesting] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      const response = await login(email);
      setOtpForTesting(response.otp_for_testing || '');
      setStep('otp');
      toast.success('OTP sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      await verifyOTP(email, otp);
      toast.success('Welcome to Berkshire Africa');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="bg-white rounded-xl p-4 inline-block mb-4">
            <img 
              src={LOGO_URL} 
              alt="Berkshire Africa" 
              className="h-12 mx-auto"
              data-testid="login-logo"
            />
          </div>
          <h1 className="font-heading text-2xl text-white tracking-tight">
            Investment Platform
          </h1>
          <p className="text-slate-400 mt-2 text-sm">
            Building Africa's Financial Infrastructure
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-white rounded-lg shadow-xl p-8 animate-fade-in stagger-1" data-testid="login-card">
          {step === 'email' ? (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="font-heading text-xl text-slate-900">Welcome Back</h2>
                <p className="text-slate-500 text-sm mt-1">
                  Sign in with your email address
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="joseph@berkshireafrica.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="email-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={loading}
                data-testid="send-otp-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ArrowRight className="h-4 w-4 mr-2" />
                )}
                Continue with Email
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOTPSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="font-heading text-xl text-slate-900">Enter Verification Code</h2>
                <p className="text-slate-500 text-sm mt-1">
                  We sent a 6-digit code to {email}
                </p>
              </div>

              <div className="flex justify-center">
                <InputOTP 
                  maxLength={6} 
                  value={otp} 
                  onChange={setOtp}
                  data-testid="otp-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {otpForTesting && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-center">
                  <p className="text-xs text-amber-700">
                    Test OTP: <span className="font-mono font-bold">{otpForTesting}</span>
                  </p>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={loading}
                data-testid="verify-otp-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Verify & Sign In
              </Button>

              <button
                type="button"
                onClick={() => { setStep('email'); setOtp(''); }}
                className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="back-to-email-btn"
              >
                Use a different email
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-6 animate-fade-in stagger-2">
          © 2025 Berkshire Africa Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
}
